import { usePortalAuth } from '@/portal/auth.store';

/**
 * Cliente HTTP do portal.
 *
 * Irmão de `@/lib/api`, e **não** o mesmo arquivo. Três diferenças que
 * justificam a duplicação de trinta linhas:
 *
 *   1. a base é `/api/v1/portal` — nenhuma chamada daqui alcança rota de
 *      operação nem por engano de digitação de caminho;
 *   2. o token vem do store do portal, guardado sob outra chave;
 *   3. importar `@/lib/api` traria `@/features/auth/auth.store` para dentro do
 *      bundle do portal, e com ele a fronteira que este arquivo existe para
 *      manter. `portal-bundle.spec.ts` reprova o build se isso acontecer.
 */

const BASE = '/api/v1/portal';

export class PortalApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'PortalApiError';
  }
}

export interface OpcoesDeChamada extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Rota pública: não envia token nem tenta renovar. */
  anonima?: boolean;
  /** Devolve a `Response` sem desserializar — para corpo binário. */
  bruta?: boolean;
}

export async function chamar<T>(caminho: string, opcoes: OpcoesDeChamada = {}): Promise<T> {
  const { anonima, bruta, body, headers, ...resto } = opcoes;

  const enviar = async (): Promise<Response> => {
    const token = anonima ? null : usePortalAuth.getState().accessToken;
    return fetch(`${BASE}${caminho}`, {
      ...resto,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };

  let resposta = await enviar();

  if (resposta.status === 401 && !anonima) {
    const renovou = await usePortalAuth.getState().renovar();
    if (renovou) resposta = await enviar();
  }

  if (!bruta && resposta.status === 204) return undefined as T;

  if (bruta) {
    if (!resposta.ok) {
      throw new PortalApiError(resposta.status, `Não foi possível baixar (${resposta.status}).`);
    }
    return resposta as unknown as T;
  }

  const texto = await resposta.text();
  const carga = texto ? (JSON.parse(texto) as unknown) : null;

  if (!resposta.ok) {
    const detalhe = carga as { message?: string } | null;
    throw new PortalApiError(
      resposta.status,
      detalhe?.message ?? `Não foi possível concluir (${resposta.status}).`,
    );
  }

  return carga as T;
}

export const buscar = <T>(caminho: string) => chamar<T>(caminho, { method: 'GET' });
export const enviar = <T>(caminho: string, body?: unknown) =>
  chamar<T>(caminho, { method: 'POST', body });

/** Upload de anexo: `multipart/form-data`, sem `content-type` declarado. */
export async function enviarArquivo<T>(caminho: string, arquivo: File): Promise<T> {
  const formulario = new FormData();
  formulario.append('file', arquivo);
  const token = usePortalAuth.getState().accessToken;

  const resposta = await fetch(`${BASE}${caminho}`, {
    method: 'POST',
    // O `content-type` fica a cargo do navegador: escrevê-lo à mão omite o
    // `boundary`, e o servidor recusa o corpo inteiro.
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: formulario,
  });

  const texto = await resposta.text();
  const carga = texto ? (JSON.parse(texto) as unknown) : null;
  if (!resposta.ok) {
    const detalhe = carga as { message?: string } | null;
    throw new PortalApiError(resposta.status, detalhe?.message ?? 'Falha ao enviar o arquivo.');
  }
  return carga as T;
}

/**
 * Baixa um anexo e entrega o arquivo ao navegador.
 *
 * Um `<a href>` simples **não funciona** aqui, e a razão é a mesma decisão que
 * torna o portal mais seguro: não há cookie de sessão. A navegação de um
 * `<a>` não carrega o cabeçalho `Authorization`, então o servidor responde
 * 401 e o cliente vê uma página de JSON no lugar do arquivo. Foi assim que a
 * primeira versão desta tela ficou — o link parecia certo e nunca baixou nada.
 *
 * A alternativa que o §c.2 descreve é URL assinada de origem separada; ela
 * exige emissão e verificação de assinatura no servidor e fica registrada
 * como o próximo passo. Até lá, `fetch` com o token e `blob:` local resolve,
 * e mantém a autorização exatamente onde ela está.
 */
export async function baixarAnexo(caminho: string, nomeDoArquivo: string): Promise<void> {
  const resposta = await chamar<Response>(caminho, {
    method: 'GET',
    // `bruta` porque o corpo é binário: passar por `JSON.parse` o destruiria.
    bruta: true,
  });

  const conteudo = await resposta.blob();
  const endereco = URL.createObjectURL(conteudo);
  try {
    const ancora = document.createElement('a');
    ancora.href = endereco;
    ancora.download = nomeDoArquivo;
    document.body.append(ancora);
    ancora.click();
    ancora.remove();
  } finally {
    // Sem isto, cada download deixa o arquivo inteiro preso na memória da aba
    // até o recarregamento.
    URL.revokeObjectURL(endereco);
  }
}
